import { Modal, ModalBody, ModalHeader, CardBody, CardTitle } from 'reactstrap'
import { IconTrash } from '@tabler/icons-react'
import { IconX } from '@tabler/icons-react'
//@ts-ignore
import { Button } from 'dsm-react-lib'
import { useAgentService } from '../agent/agentService'
import { toast } from 'react-toastify'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

interface DeleteAgentModalProps {
  isOpen: boolean
  toggle: () => void
  agent: any
  // Called after a successful delete with the freshly-fetched team agents list
  onDeleted?: (agents?: any[]) => void
}

const DeleteAgentModal: React.FC<DeleteAgentModalProps> = ({
  isOpen,
  toggle,
  agent,
  onDeleted
}) => {
  const {
    isDeletingAgent,
    deleteAgent,
    isGettingMyAgents,
    getAgentsListByUser,
    isGettingTeamsAgents,
    getTeamAgentsList
  } = useAgentService()
  const navigate = useNavigate()
  const location = useLocation()
  const { teamId } = useParams<{ teamId?: string }>()

  const handleDelete = async (agentId: string) => {
    if (!agentId) return

    try {
      const response = await deleteAgent(agentId)
      // Refresh data based on location
      if (location?.pathname.includes('team') && teamId) {
        const teamAgentsResponse = await getTeamAgentsList(teamId)
        onDeleted?.(teamAgentsResponse?.agents)
      } else {
        await getAgentsListByUser()
        onDeleted?.()
      }

      toggle()

      if (response?.status === 200) {
        toast.success('Agent deleted successfully')
      } else {
        toast.error('Error deleting agent')
      }

      const targetRoute = location?.pathname.includes('teams')
        ? `/teams/${teamId}`
        : '/manage-agents'
      navigate(targetRoute)
    } catch (error) {
      toggle()
      toast.error('Error deleting agent')
      console.error('Delete agent error:', error)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      contentClassName="theme-surface border theme-border-default overflow-hidden"
      data-testid="delete-agent-modal">
      <ModalHeader
        className="bg-primary !justify-between !border-0 !flex !items-center"
        toggle={toggle}
        close={
          <IconX
            size={24}
            onClick={toggle}
            className="text-white transition-colors duration-300 ease-in-out hover:text-white cursor-pointer"
            data-testid="close-button"
          />
        }>
        <div className="flex items-center text-[var(--white)]">
          <IconTrash size={22} className="me-1" />
          <span className="text-[19px] font-semibold leading-none">Delete Agent?</span>
        </div>
      </ModalHeader>
      <ModalBody className="theme-surface !border-0 !py-1">
        <div className="text-center border-0">
          <CardBody className="px-0 py-1">
            <CardTitle tag="h4" className="theme-text-heading mb-0">
              Are you sure you want to delete <b>{agent?.name}</b>? This action cannot be undone.
            </CardTitle>
          </CardBody>
        </div>
        <div className="d-flex justify-content-end gap-1 my-1">
          <Button
            label="Cancel"
            variant="outline"
            color="neutral"
            className="dsm-btn-neutral"
            onClick={toggle}
          />
          <Button
            label={
              isDeletingAgent || isGettingMyAgents || isGettingTeamsAgents
                ? 'Deleting...'
                : 'Confirm'
            }
            color="danger"
            disabled={isDeletingAgent || isGettingMyAgents || isGettingTeamsAgents}
            onClick={() => handleDelete(agent?.id)}
          />
        </div>
      </ModalBody>
    </Modal>
  )
}

export default DeleteAgentModal
